import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
  Box,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CallIcon from "@mui/icons-material/Call";
import { useSnackbar } from "../../App";
import Color from "../../components/Color";


const AddCustomer = ({ open, onClose, formData, setFormData, onSubmit, phoneError }) => {
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});

  // Update errors state when phoneError prop changes
  React.useEffect(() => {
    if (phoneError) {
      setErrors((prev) => ({ ...prev, phone: phoneError }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
    }
  }, [phoneError]);

  const validate = () => {
    const newErrors = {};

    if (!(formData.name || "").trim()) {
      newErrors.name = "Customer name is required";
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = "Only letters are allowed";
    }

    const phone = (formData.phone || "").trim();
    if (!phone) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(phone)) {
      newErrors.phone = "Phone must be 10 digits";
    } else if (/^(\d)\1{9}$/.test(phone)) {
      newErrors.phone = "Invalid phone number";
    }

    if (!(formData.address || "").trim()) {
      newErrors.address = "Address is required";
    }

    if (!(formData.contact_type || "").trim()) {
      newErrors.contact_type = "Contact type is required";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      try {
        await onSubmit(formData);
        handleCancel();
      } catch (error) {
        console.error("Submit failed:", error);
        if (error.response?.data?.errors?.phone) {
          setErrors((prev) => ({ ...prev, phone: error.response.data.errors.phone[0] }));
          showSnackbar(error.response.data.errors.phone[0], "error");
        } else {
          showSnackbar(error.response?.data?.message || "Something went wrong. Please try again.", "error");
        }
      }
    } else {
      showSnackbar("Please fix validation errors before submitting.", "error");
    }
  };

  const handleCancel = () => {
    setErrors({});
    setFormData({
      id: null,
      name: "",
      phone: "",
      address: "",
      contact_type: "",
    });
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const filteredValue = value.replace(/[^a-zA-Z ]/g, "");
      setFormData({ ...formData, [name]: filteredValue });
      if (/[^a-zA-Z ]/.test(value)) {
        setErrors({ ...errors, name: "Only letters are allowed" });
      } else if (!value.trim()) {
        setErrors({ ...errors, name: "Customer name is required" });
      } else {
        setErrors({ ...errors, name: "" });
      }
      return;
    }

    if (name === "phone") {
      const filteredValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setFormData({ ...formData, [name]: filteredValue });
      if (/[^0-9]/.test(value)) {
        setErrors({ ...errors, phone: "Only numbers are allowed" });
      } else if (filteredValue.length !== 10) {
        setErrors({ ...errors, phone: "Phone must be 10 digits" });
      } else if (/^(\d)\1{9}$/.test(filteredValue)) {
        setErrors({ ...errors, phone: "Invalid phone number" });
      } else {
        setErrors({ ...errors, phone: "" });
      }
      return;
    }

    setFormData({ ...formData, [name]: value });
    if (!value.trim() && ["address", "contact_type"].includes(name)) {
      setErrors({
        ...errors,
        [name]: `${name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} is required`,
      });
    } else {
      setErrors({ ...errors, [name]: "" });
    }
  };

  

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCancel();
        }
      }}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle>{formData.id ? "Edit Customer" : "Add Customer"}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          maxHeight: { xs: "60vh", sm: "auto" },
          overflowY: "auto",
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 1,
            }}
          >
            {/* Customer Name */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Customer Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                placeholder="Enter customer name"
                fullWidth
                error={!!errors.name}
                helperText={errors.name}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{ "& .MuiInputLabel-root": { display: "none" } }}
              />
            </Box>

            {/* Contact Type */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Contact Type*
              </Typography>
              <RadioGroup
                row
                name="contact_type"
                value={formData.contact_type || ""}
                onChange={handleChange}
              >
                <FormControlLabel
                  value="whatsapp"
                  control={
                    <Radio
                      sx={{
                        color: Color.primary.main,
                        "&.Mui-checked": {
                          color: Color.primary.main,
                        },
                      }}
                    />
                  }
                  label="WhatsApp"
                />
                <FormControlLabel
                  value="mobile"
                  control={
                    <Radio
                      sx={{
                        color: Color.primary.main,
                        "&.Mui-checked": {
                          color: Color.primary.main,
                        },
                      }}
                    />
                  }
                  label="Mobile"
                />
              </RadioGroup>
              {errors.contact_type && (
                <Typography color="error" variant="caption">
                  {errors.contact_type}
                </Typography>
              )}
            </Box>

            {/* Phone */}
            <Box mb={1}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                {formData.contact_type === "whatsapp"
                  ? "WhatsApp Number*"
                  : formData.contact_type === "mobile"
                  ? "Mobile Number*"
                  : "Phone*"}
              </Typography>
              <TextField
                name="phone"
                value={formData.phone || ""}
                onChange={handleChange}
                placeholder={
                  formData.contact_type === "whatsapp"
                    ? "Enter WhatsApp number"
                    : formData.contact_type === "mobile"
                    ? "Enter mobile number"
                    : "Enter phone number"
                }
                fullWidth
                error={!!errors.phone}
                helperText={errors.phone}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{ "& .MuiInputLabel-root": { display: "none" } }}
                autoComplete="off"
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {formData.contact_type === "whatsapp" ? (
                        <WhatsAppIcon sx={{ color: "green" }} />
                      ) : formData.contact_type === "mobile" ? (
                        <CallIcon color="primary" />
                      ) : null}
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Address */}
            <Box mb={1.5}>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: "13px",
                }}
              >
                Address*
              </Typography>
              <TextField
                name="address"
                value={formData.address || ""}
                onChange={handleChange}
                placeholder="Enter address"
                fullWidth
                multiline
                rows={4}
                error={!!errors.address}
                helperText={errors.address}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Submit and Cancel Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} mt={1} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              sx={{ background: Color.primary.main, color: "white" }}
            >
              {formData.id ? "Update" : "Submit"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCustomer;